import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FolderOpen, Folder, AlertCircle } from "lucide-react";
import DeskPanel from "./DeskPanel";
import { desk, fontMicro } from "./tokens";
import { PaperPatina } from "../realism/PaperPatina";

const _TEX = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/textures` : "/textures";

const _paperVar = (seed) => {
  let h = 0; const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % 6) + 1;
};
const _paperBg = (seed) => ({
  backgroundImage: `url("${_TEX}/paper-0${_paperVar(seed)}.png")`,
  // 150% over-scale — see PaperPatina.jsx for the "mini table" fix.
  backgroundSize: "150% 150%",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
});

const CaseFileCard = ({ topic }) => {
  if (topic.missing) {
    return (
      <div
        className="border p-3 flex items-center gap-2 opacity-60"
        style={{ borderColor: desk.edge, borderStyle: "dashed" }}
        data-testid={`case-file-${topic.slug}`}
      >
        <AlertCircle size={12} style={{ color: desk.inkMuted }} />
        <span className={fontMicro} style={{ color: desk.inkMuted }}>
          {topic.slug} · ARCHIVED
        </span>
      </div>
    );
  }
  const updated = (topic.updated_at || "").slice(0, 10);
  return (
    <div
      className="group relative paper-folder p-4 pl-8 transition-all overflow-hidden"
      style={_paperBg(topic.slug)}
      data-testid={`case-file-${topic.slug}`}
    >
      <PaperPatina seed={`casefile-${topic.slug}`} variant="folder" intensity={0.85} />
      <div className="flex items-center justify-between gap-2">
        <div className={`${fontMicro} flex items-center gap-1.5`} style={{ color: "#4a3a1e" }}>
          <Folder size={11} />
          <span>CASE FILE</span>
          {topic.category && (
            <>
              <span style={{ color: "#8c7345" }}>·</span>
              <span style={{ color: "#5a4530" }}>{topic.category.toUpperCase()}</span>
            </>
          )}
        </div>
        {topic.is_new && (
          <span
            className="font-mono text-[8px] uppercase tracking-[0.3em] border px-1.5 py-0.5 animate-pulse"
            style={{ color: "#B02418", borderColor: "#B0241888", backgroundColor: "rgba(176,36,24,0.10)" }}
            data-testid={`case-file-new-${topic.slug}`}
          >
            NEW EVIDENCE
          </span>
        )}
      </div>

      <h3
        className="font-display text-lg mt-3 leading-snug"
        style={{ color: "#1a1208" }}
      >
        {topic.title}
      </h3>
      {topic.tagline && (
        <p className="text-sm mt-2 line-clamp-2" style={{ color: "#3a2c18" }}>
          {topic.tagline}
        </p>
      )}

      <div className={`${fontMicro} mt-4 flex items-center justify-between`}
           style={{ color: "#4a3a1e" }}>
        <span>LAST UPDATE · {updated || "—"}</span>
        {typeof topic.controversy_score === "number" && (
          <span style={{ color: "#B02418" }}>
            CTRL {topic.controversy_score.toString().padStart(2, "0")}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Link
          to={`/topic/${topic.slug}`}
          className="inline-flex items-center gap-1.5 border px-2.5 py-1.5 transition-colors hover:border-[#B02418] hover:text-[#B02418]"
          style={{ borderColor: "#8c7345", color: "#1a1208", background: "rgba(255,250,240,0.35)" }}
          data-testid={`case-file-open-${topic.slug}`}
        >
          <FolderOpen size={11} />
          <span className={fontMicro}>OPEN CASE FILE</span>
        </Link>
        <Link
          to={`/topic/${topic.slug}`}
          className={`${fontMicro} inline-flex items-center gap-1 hover:text-[#B02418]`}
          style={{ color: "#5a4530" }}
          data-testid={`case-file-continue-${topic.slug}`}
        >
          CONTINUE READING <ChevronRight size={11} />
        </Link>
      </div>
    </div>
  );
};

const Empty = ({ children }) => (
  <div
    className="border border-dashed p-6 text-center"
    style={{ borderColor: desk.edge, color: desk.inkMuted }}
  >
    <div className={fontMicro}>{children}</div>
  </div>
);

const CaseFilesSection = ({ caseFiles = [] }) => {
  return (
    <DeskPanel
      title="MY CASE FILES"
      count={caseFiles.length}
      testid="desk-case-files"
    >
      {caseFiles.length === 0 ? (
        <Empty>
          No active case files yet — open the Archive and follow an investigation to pin it here.
        </Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {caseFiles.map((t) => (
            <CaseFileCard key={t.slug} topic={t} />
          ))}
        </div>
      )}
    </DeskPanel>
  );
};

export default CaseFilesSection;
